import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { deriveAgencyNav } from './agency-nav.logic.js';

import { useAuthStore } from '@/stores/auth.store';

/**
 * Sends an agency tenant's users to their own product instead of the HR
 * dashboard.
 *
 * The walkthrough on 2026-09-19 had an `account_manager` sign in and land on
 * "Good afternoon" over Quick Access tiles for Leave, Payroll, Attendance,
 * Recruitment, Expenses and Employees. Seven of that page's API calls answer
 * 403 for the role — including `/api/notifications`, so the person who is
 * supposed to notice a designer handing work back cannot be notified at all.
 *
 * Swapping the sidebar alone was not enough: the landing route is what someone
 * actually sees first, and it was still the HR dashboard.
 *
 * The predicate is `deriveAgencyNav(...) !== null` rather than a second
 * "is this an agency" rule, so the shell and the landing route can never
 * disagree about what an agency tenant is.
 */
export function AgencyLandingGate({ children }: { children: ReactNode }): ReactNode {
  const user = useAuthStore((s) => s.user);
  const isAgency = deriveAgencyNav(user?.modules ?? [], user?.roles ?? []) !== null;

  // `replace`, so Back does not bounce the user between the two landings.
  if (isAgency) return <Navigate to="/agency" replace />;

  return <>{children}</>;
}
